import React, { useState, useEffect, useCallback } from 'react';
import {
  DeleteForever
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  DisablableOption,
  ModelBase,
  UpsertableBase,
} from "@models";
import { 
  GameClass,
  CharacterLimitation,
  Spell, 
} from "@dndModels";
import { 
  ABILITY_SCORES,
  CHARACTER_LIMITATION_OPTIONS,
  CHARACTER_LIMITATION_TYPES_SANS_VARIANT_SPECIFIC,
  CLASS_GROUPS,
  FEATURE_SOURCE_TYPE_OPTIONS,
  SCHOOLS
} from "@dndModels/Constants";
import { CharacterLimitationType } from "@dndModels/Types";

import API from "@dndUtilities/system_api";

import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";

// import DisplayObjects from '../display/DisplayObjects';
import LoadingBox from "@components/input/LoadingBox";
import SelectSpellBox from "@dndComponents/select/SelectSpellBox";
import StringsBox from "@components/input/strings/StringsBox";
import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";

interface Props {
  obj: CharacterLimitation;
  change_time?: number;
  onChange: (changed: CharacterLimitation) => void; 
  onDelete?: () => void; 
  disabled?: boolean;
  types?: string[];
}

const api = API.getInstance();

export default function CharacterLimitationBox({
  obj,
  change_time = 0,
  onChange,
  onDelete,
  disabled = false,
  types
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [game_classes, setGameClasses] = useState<GameClass[]>([]);
  const [type_changing, setTypeChanging] = useState(false);
  const [prev_change_time, setChangeTime] = useState(change_time);
  const [variant_specific, setVariantSpecific] = useState(obj.variant_specific);

  const load = useCallback( async () => {
    setLoading(true);
    const res = await api.getSetOfObjects({ data_types: ["game_class"] });
    setGameClasses(res.game_class as GameClass[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (type_changing) {
      setTypeChanging(false);
    }
  }, [type_changing]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  useEffect(() => {
    if (prev_change_time !== change_time) {
      setChangeTime(change_time);
    }
  }, [prev_change_time, change_time]);

  const render_class_or_class_group = () => {
    const options: DisablableOption[] = game_classes.filter(o => o.is_true_dominant).map(o => { 
      return { 
        id: o.variant_group_id,
        text: o.use_name,
        disabled: false,
        reason: ""
      };
    });
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name="Class or Class Group"
            options={options}
            extra_options={CLASS_GROUPS}
            values={obj.ids}
            multiple
            disabled={disabled}
            onChange={(changed: string[]) => {
              obj.ids = changed;
              onChange(obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox
            name="Level"
            value={`${obj.values[0]}`}
            disabled={disabled}
            type="number"
            onBlur={(changed: string) => {
              obj.values[0] = +changed;
              onChange(obj);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderDetailOptions = () => {
    // debugger;
    if (obj.type === "") {
      return <></>;
    } else if (obj.type === "alignment") {
      return (
        <SelectStringBox
          name="Alignments"
          values={obj.ids}
          options={["Lawful Good","Lawful Neutral","Lawful Evil","Neutral Good","True Neutral","Neutral Evil","Chaotic Good","Chaotic Neutral","Chaotric Evil","Lawful Any","Neutral Any","Chaotic Any","Any Good","Any Neutral","Any Evil","Not Lawful","Not Lawfully Neutral","Not Chaotic","Not Good","Not Morally Neutral","Not Evil"]}
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => {
            obj.ids = changed;
            onChange(obj);
            setReloading(true);
          }}
        />
      );
    } else if (obj.type === "min_ability_score") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Ability Scores"
              values={obj.ids}
              options={ABILITY_SCORES}
              multiple
              disabled={disabled}
              onChange={(changed: string[]) => {
                obj.ids = changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Minimum Value"
              value={`${obj.values[0]}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                obj.values[0] = +changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (obj.type === "spellcaster") {
      return (
        <SelectStringBox
          name="Spellcasting Types"
          values={obj.ids}
          options={["Spellcasting","Pact Magic","At least 1 Spell","Ritual"]}
          multiple
          disabled={disabled}
          onChange={(changed: string[]) => {
            obj.ids = changed;
            onChange(obj);
            setReloading(true);
          }}
        />
      );
    } else if (obj.type === "spells_of_schools") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Schools"
              values={obj.ids}
              options={["Any",...SCHOOLS]}
              multiple
              disabled={disabled}
              onChange={(changed: string[]) => {
                obj.ids = changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="How Many Spells?"
              value={`${obj.values[0]}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                obj.values[0] = +changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Minimum Spell Level"
              value={`${obj.values[1]}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                obj.values[1] = +changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (obj.type === "game_class" || obj.type === "subclass") {
      if (variant_specific) {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox
                name="Detail"
                data_type={obj.type}
                values={obj.ids}
                multiple
                disabled={disabled}
                // use_width_finder
                auto_sort
                onChange={(changed: string[]) => {
                  obj.ids = changed;
                  onChange(obj);
                  setReloading(true);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Level"
                value={`${obj.values[0]}`}
                disabled={disabled}
                type="number"
                onBlur={(changed: string) => {
                  obj.values[0] = +changed;
                  onChange(obj);
                  setReloading(true);
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        return (
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <SelectObjectBox
                name="Detail"
                data_type={obj.type}
                values={obj.ids}
                extra_filter={(o: UpsertableBase) => {
                  return o instanceof ModelBase && o.is_true_dominant;
                }}
                use_field="variant_group_id"
                multiple
                disabled={disabled}
                // use_width_finder
                auto_sort
                onChange={(changed: string[]) => {
                  obj.ids = changed;
                  onChange(obj);
                  setReloading(true);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12
              }}>
              <StringBox
                name="Level"
                value={`${obj.values[0]}`}
                disabled={disabled}
                type="number"
                onBlur={(changed: string) => {
                  obj.values[0] = +changed;
                  onChange(obj);
                  setReloading(true);
                }}
              />
            </Grid>
          </Grid>
        );
      }
    } else if (obj.type === "class_group") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <SelectStringBox
              name="Class Group"
              options={CLASS_GROUPS}
              values={obj.ids}
              multiple
              disabled={disabled}
              onChange={(changed: string[]) => {
                obj.ids = changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 12
            }}>
            <StringBox
              name="Level"
              value={`${obj.values[0]}`}
              disabled={disabled}
              type="number"
              onBlur={(changed: string) => {
                obj.values[0] = +changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (obj.type === "class_or_class_group") {
      return render_class_or_class_group();
    } else if (obj.type === "level") {
      return (
        <StringBox
          name="Level"
          value={`${obj.values[0]}`}
          disabled={disabled}
          type="number"
          onBlur={(changed: string) => {
            obj.values[0] = +changed;
            onChange(obj);
            setReloading(true);
          }}
        />
      );
    } else if (obj.type === "proficiency") {
      return (
        <>
          <SelectObjectBox
            name="Detail"
            data_types={["skill","tool","armor_type","weapon_keyword","item"]}
            values={obj.ids}
            filters={{
              "item": { item_type: "Weapon" }
            }}
            limit={100}
            // internal_filter
            multiple
            disabled={disabled}
            // use_width_finder
            auto_sort
            // log_it
            use_field="variant_group_id"
            onChange={(changed: string[]) => {
              obj.ids = changed;
              onChange(obj);
              setReloading(true);
            }}
          />
        </>
      );
    } else if (obj.type === "at_least_1_weapon") {
      return (
        <SelectObjectBox
          name="Keyword"
          data_types={["weapon_keyword","item"]}
          values={obj.ids}
          filters={{
            "item": { item_type: "Weapon" }
          }}
          limit={100}
          // internal_filter
          multiple
          disabled={disabled}
          // use_width_finder
          auto_sort
          use_field="variant_group_id"
          onChange={(changed: string[]) => {
            obj.ids = changed;
            onChange(obj);
            setReloading(true);
          }}
        />
      );
    } else if (obj.type === "at_least_1_language") {
      return (<></>);
    } else if (obj.type === "spell") {
      return (
        <SelectSpellBox
          name="Specific Spells" 
          values={obj.ids} 
          extra_filter={(o: Spell) => {
            return o.is_true_dominant;
          }}
          multiple
          disabled={disabled}
          // log_it
          onChange={(changed: string[]) => {
            obj.ids = changed;
            onChange(obj);
            setReloading(true);
          }} 
        />
      );
    } else if (obj.type === "tags") {
      return (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox
              name="From Source Types"
              options={FEATURE_SOURCE_TYPE_OPTIONS}
              values={obj.from_source_types}
              disabled={disabled}
              multiple
              onChange={(changed: string[]) => {
                obj.from_source_types = changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
          <Grid
            size={{
              xs: 6
            }}>
            <StringsBox
              name="Tags"
              values={obj.ids}
              disabled={disabled}
              onChange={(changed: string[]) => {
                obj.ids = changed;
                onChange(obj);
                setReloading(true);
              }}
            />
          </Grid>
        </Grid>
      );
    } else if (obj.type === "dragonmark") {
      return <>The Character cannot have any other Dragonmarks.</>;
    } else if (obj.type === "wild_talent") {
      return <>The Character cannot have any other Wild Talents.</>;
    } else {
      if (variant_specific) {
        return (
          <SelectObjectBox
            name="Detail"
            data_type={obj.type}
            values={obj.ids}
            limit={100}
            // internal_filter
            multiple
            disabled={disabled}
            // use_width_finder
            auto_sort
            onChange={(changed: string[]) => {
              obj.ids = changed;
              onChange(obj);
              setReloading(true);
            }}
          />
        );
      } else {
        return (
          <SelectObjectBox
            name="Detail"
            data_type={obj.type}
            values={obj.ids}
            limit={100}
            extra_filter={(o: UpsertableBase) => {
              return o instanceof ModelBase && o.is_true_dominant;
            }}
            // internal_filter
            multiple
            disabled={disabled}
            // use_width_finder
            auto_sort
            onChange={(changed: string[]) => {
              obj.ids = changed;
              onChange(obj);
              setReloading(true);
            }}
          />
        );
      }
    }
  }

  if (!fetched || loading || type_changing) {
    return (<LoadingBox />);
  } else {
    // console.log(obj.toDetailString());
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 2
          }}>
          { !disabled && onDelete &&
            <Tooltip title={`Delete`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={onDelete}>
                <DeleteForever/>
              </Fab>
            </Tooltip> 
          }
        </Grid>
        <Grid
          size={{
            xs: 10
          }}>
          { !reloading && <DisplayStringWithReplacements display_string={obj.toDetailString()} /> }
        </Grid>
        <Grid
          size={{
            xs: 2
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 12
              }}>
              <ToggleButtonBox 
                name="Exclusive"
                value={obj.exclusive}
                disabled={disabled}
                onToggle={() => {
                  obj.exclusive = !obj.exclusive;
                  onChange(obj);
                }}
              />
            </Grid>
            { !CHARACTER_LIMITATION_TYPES_SANS_VARIANT_SPECIFIC.includes(obj.type) &&
              <Grid
                size={{
                  xs: 12
                }}>
                <ToggleButtonBox 
                  name="Variant Specific"
                  value={obj.variant_specific}
                  disabled={disabled}
                  onToggle={() => {
                    obj.variant_specific = !obj.variant_specific;
                    obj.ids = [];
                    onChange(obj);
                    setVariantSpecific(obj.variant_specific);
                    setTypeChanging(true);
                  }}
                />
              </Grid>
            }
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <SelectStringBox
            name="Type"
            value={obj.type}
            options={ types ? CHARACTER_LIMITATION_OPTIONS.filter(o => types.includes(o.id)) : CHARACTER_LIMITATION_OPTIONS }
            disabled={disabled}
            onChange={(changed: CharacterLimitationType) => {
              obj.type = changed;
              obj.ids = [];
              switch (obj.type) {
                case "min_ability_score": obj.values = [13]; break;
                case "spells_of_schools": obj.values = [1, 0]; break;
                case "level": obj.values = [1]; break;
                case "game_class": obj.values = [1]; break;
                case "class_group": obj.values = [1]; break;
                case "class_or_class_group": obj.values = [1]; break;
                case "subclass": obj.values = [1]; break;
                default: obj.values = []; break;
              }
              if (CHARACTER_LIMITATION_TYPES_SANS_VARIANT_SPECIFIC.includes(changed)) {
                obj.variant_specific = false;
              }
              onChange(obj);
              setTypeChanging(true);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { renderDetailOptions() }
        </Grid>
      </Grid>
    );
  }
}
